import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import { Button } from '@components/ui/Button';
import { Avatar } from '@/components/ui';
import { adapterConfig } from '@/api/config';
import {
  useNotifications,
  useMarkAllNotificationsRead,
  useMarkNotificationRead,
} from '@hooks/useNotifications';
import { notificationLink } from '@/types/current-notification';

interface NotificationPopoverProps {
  open: boolean;
  onClose: () => void;
  streamStatus?: string;
}
function formatTimeAgo(seconds: number) {
  const elapsed = Math.max(0, Math.floor(Date.now() / 1000) - seconds);
  if (elapsed < 60) return 'Just now';
  if (elapsed < 3600) return Math.floor(elapsed / 60) + 'm ago';
  if (elapsed < 86400) return Math.floor(elapsed / 3600) + 'h ago';
  return Math.floor(elapsed / 86400) + 'd ago';
}
export function NotificationPopover({
  open,
  onClose,
  streamStatus,
}: NotificationPopoverProps) {
  const panelRef = useRef<HTMLDivElement>(null);
  const [showAll, setShowAll] = useState(false);
  const query = useNotifications();
  const markAll = useMarkAllNotificationsRead();
  const markOne = useMarkNotificationRead();
  const notifications = query.data ?? [];
  const displayed = showAll ? notifications : notifications.slice(0, 5);
  const unread = displayed.filter((n) => n.readAt === null);
  const busy = markAll.isPending || markOne.isPending;
  const error = markAll.error ?? markOne.error;
  useEffect(() => {
    if (!open) return;
    const previous = document.activeElement as HTMLElement | null;
    panelRef.current?.focus();
    const handleKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    const handleOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      if (
        !panelRef.current?.contains(target) &&
        !target.closest('button[aria-controls="notification-panel"]')
      )
        onClose();
    };
    document.addEventListener('keydown', handleKey);
    document.addEventListener('mousedown', handleOutside);
    return () => {
      document.removeEventListener('keydown', handleKey);
      document.removeEventListener('mousedown', handleOutside);
      previous?.focus();
    };
  }, [open, onClose]);
  if (!open) return null;
  return createPortal(
    <div
      ref={panelRef}
      id="notification-panel"
      tabIndex={-1}
      className="notificationsContainer text-left"
      role="dialog"
      aria-label="Notifications"
    >
      <div className="flex w-full items-center justify-between gap-4">
        <h2 className="text-xl font-semibold text-[--text-dark-primary]">
          Notifications
        </h2>
        <div className="flex items-center gap-4">
          <Button variant="secondary" onClick={() => setShowAll(!showAll)}>
            {showAll ? 'Show recent' : 'View All'}
          </Button>
          <Button
            variant="secondary"
            aria-label="Mark displayed notifications as read"
            title="Mark displayed notifications as read"
            disabled={busy || query.isError || !unread.length}
            onClick={() => markAll.mutate(unread.map((n) => n.notificationId))}
            className="w-11 p-0"
          >
            <i aria-hidden="true" className="ri-check-double-line text-2xl" />
          </Button>
        </div>
      </div>
      {streamStatus && (
        <p
          role="status"
          className={
            streamStatus === 'Live updates connected'
              ? 'sr-only'
              : 'text-sm text-[--text-dark-tertiary]'
          }
        >
          {streamStatus}
        </p>
      )}
      {(query.isError ||
        error ||
        (streamStatus && streamStatus !== 'Live updates connected')) && (
        <Button
          variant="link"
          disabled={query.isFetching}
          onClick={() => {
            markAll.reset();
            markOne.reset();
            void query.refetch();
          }}
        >
          Refresh notifications
        </Button>
      )}
      {query.isError && (
        <p role="alert">
          Notifications could not be refreshed: {query.error.message}
        </p>
      )}
      {error && (
        <p role="alert">
          {error.message} Some displayed notifications may already be read;
          refresh before retrying.
        </p>
      )}
      {query.isPending ? (
        <p role="status">Loading notifications…</p>
      ) : !notifications.length && !query.isError ? (
        <p>No notifications</p>
      ) : (
        <div className="notificationsContent min-h-0">
          {[false, true].map((read) => {
            const items = displayed.filter((n) => (n.readAt !== null) === read);
            return (
              items.length > 0 && (
                <section key={String(read)} className="contentSection">
                  <h3 className="sectionHeader">{read ? 'Earlier' : 'New'}</h3>
                  {items.map((notification) => {
                    const href = notificationLink(notification);
                    const kind = notification.event.kind;
                    const title =
                      kind === 'ProjectCreated'
                        ? 'New project proposal'
                        : kind === 'ProposalSubmitted'
                          ? 'Project scope ready for review'
                          : kind === 'MilestoneCompletionRequested'
                            ? 'Milestone submitted!'
                            : kind.replace(/([a-z])([A-Z])/g, '$1 $2');
                    const description =
                      kind === 'ProjectCreated'
                        ? 'A project has been assigned for planning.'
                        : kind === 'ProposalSubmitted'
                          ? 'Review the proposed milestones and cost.'
                          : kind === 'MilestoneCompletionRequested'
                            ? 'A milestone has been submitted for client review.'
                            : undefined;
                    return (
                      <article
                        key={notification.notificationId}
                        className="notificationItem !cursor-default"
                      >
                        <div className="avatar relative">
                          <Avatar
                            size="lg"
                            alt={
                              notification.actor?.displayName ??
                              'Notification author'
                            }
                            src={
                              notification.actor?.imageUrl
                                ? adapterConfig.baseURL.replace(/\/api$/, '') +
                                  notification.actor.imageUrl
                                : undefined
                            }
                          />
                          {!read && <span className="dot" />}
                        </div>
                        <div className="content min-w-0">
                          <div className="contentTitle">
                            <span className="title break-words">{title}</span>
                            {!read && (
                              <Button
                                variant="ghost"
                                className="button"
                                size="sm"
                                disabled={busy || query.isError}
                                aria-label={'Mark ' + title + ' as read'}
                                onClick={() =>
                                  markOne.mutate(notification.notificationId)
                                }
                              >
                                <i
                                  aria-hidden="true"
                                  className="ri-check-double-line text-xl"
                                />
                              </Button>
                            )}
                          </div>
                          {description && (
                            <p className="text-base text-[--text-dark-secondary]">
                              {notification.actor?.displayName && (
                                <strong>
                                  {notification.actor.displayName}:{' '}
                                </strong>
                              )}
                              {description}
                            </p>
                          )}
                          {notification.projectTitle && (
                            <p className="text-base text-[--text-dark-secondary]">
                              {notification.projectTitle}
                            </p>
                          )}
                          <time
                            className="contentDate"
                            dateTime={new Date(
                              notification.event.occurredAt * 1000
                            ).toISOString()}
                          >
                            {formatTimeAgo(notification.event.occurredAt)}
                          </time>
                          <div className="flex flex-wrap justify-end gap-3">
                            {href && (
                              <Link
                                onClick={onClose}
                                className="rounded-[12px] bg-primary px-3 py-2 text-sm font-semibold text-[--text-light-primary]"
                                to={href}
                              >
                                {kind === 'MilestoneCompletionRequested'
                                  ? 'Review Milestone'
                                  : href.startsWith('/projects/')
                                    ? 'Review Project'
                                    : 'View details'}
                              </Link>
                            )}
                          </div>
                        </div>
                      </article>
                    );
                  })}
                </section>
              )
            );
          })}
        </div>
      )}
    </div>,
    document.body
  );
}
